import * as Amplitude from '@amplitude/analytics-browser';
import startCase from 'lodash.startcase';

import { UserOutput } from 'dtos/users/UserOutput';
import { amplitudeApiKey } from 'utils/config';

export type AmplitudeEvent =
  | 'Diligence Details'
  | 'View Report Add-Ons'
  | 'Add Add-On'
  | 'Remove Add-On'
  | 'Send Add-On Request'
  | 'Inspection Photo'
  | 'View Underwriting Page'
  | 'View Underwriting Dashboard'
  | 'Create Underwriting Property'
  | 'View Comp Database';

class AmplitudeService {
  private initialized = false;
  private instance: Amplitude.Types.BrowserClient | undefined;

  async initialize(): Promise<void> {
    try {
      if (!amplitudeApiKey) {
        console.warn('AmplitudeService > Missing API key');
        return;
      }

      if (this.initialized) {
        console.warn('AmplitudeService > Already initialized');
        return;
      }

      this.instance = Amplitude.createInstance();

      await this.instance.init(amplitudeApiKey, {
        autocapture: {
          pageViews: true,
          sessions: true,
          elementInteractions: false,
          attribution: false,
          formInteractions: false,
          fileDownloads: false,
        },
      }).promise;

      this.initialized = true;
    } catch (error) {
      console.error('AmplitudeService > Failed to initialize', error);
      this.initialized = false;
    }
  }

  setUser(user?: UserOutput): void {
    try {
      if (!this.initialized || !this.instance) {
        console.warn('AmplitudeService > Operation `setUser` - Service is not initialized');
        return;
      }

      if (!user) {
        return;
      }

      this.instance.setUserId(user.email);

      const identify = new Amplitude.Identify()
        .set('name', user.fullName ?? '')
        .set('email', user.email ?? user.username)
        .set('companyName', user.companyName ?? '')
        .set('jobRole', user.jobRole ?? '')
        .set('hasCostarLicense', user.hasCostarLicense ?? false)
        .set('roles', user.roles ?? []);

      this.instance.identify(identify);
    } catch (error) {
      console.error('AmplitudeService > Operation `setUser` - Failed to set user', error);
    }
  }

  trackEvent(event: AmplitudeEvent, properties: Record<string, any> = {}) {
    try {
      if (!this.initialized || !this.instance) {
        console.warn('AmplitudeService > Operation `trackEvent` - Service is not initialized');
        return;
      }

      if (!event) {
        return;
      }

      const renamedProperties: Record<string, any> = {};
      for (const prop in properties) {
        renamedProperties[startCase(prop)] = properties[prop];
      }

      this.instance.track(event, renamedProperties);
    } catch (error) {
      console.error('AmplitudeService > Operation `trackEvent` - Failed to set user', error);
    }
  }
}

const AmplitudeServiceInstance = new AmplitudeService();
export default AmplitudeServiceInstance;
